/**
 * Las capturas pegadas en el modal de alta, en espera de que la operacion
 * exista.
 *
 * El contrato sube a `POST /trades/{id}/images` y ese `id` no existe hasta que
 * el formulario se guarda, asi que en el alta no hay donde subir nada. Lo que
 * si se puede es RECOGER lo pegado y entregarlo: al publicar, el modal navega
 * al detalle y la zona de subida de alli lo encuentra aqui y lo mete en su cola,
 * con su progreso por archivo y su reintento.
 *
 * Asi el camino de subida sigue siendo uno solo —el del detalle, que es el que
 * tiene donde pintar el progreso y donde explicar un fallo— y el pegado en el
 * alta no inventa un segundo.
 *
 * Lo retenido lleva el `id` de SU operacion, y solo el detalle de esa operacion
 * se lo lleva. Sin el id, el primer detalle con permiso de escritura que se
 * montara se quedaba con las capturas de otra (BUGLOG, 2026-09-11): bastaba con
 * que el navegador no llegara al detalle recien creado, o llegara a uno donde
 * no se puede escribir, para que se subieran a la siguiente operacion abierta.
 *
 * Vive en el modulo y no en el estado de React porque el traspaso ocurre
 * exactamente cuando el modal se desmonta. Un `File` no sobrevive a una recarga
 * de la pagina, y no tiene por que: si eso pasa, se vuelve a pegar.
 */

/**
 * Un archivo y, si el alta le hizo una vista previa, su URL de objeto: lo que se
 * descarta la revoca, porque una URL de objeto retiene el archivo en memoria
 * hasta que alguien la suelta.
 */
export interface PendingShot<TFile> {
  readonly file: TFile;
  readonly previewUrl?: string;
}

/**
 * Generico sobre el tipo de archivo para poder recorrerlo con `node --test`,
 * que no tiene `File` ni `URL.revokeObjectURL` de navegador. La logica es la
 * misma con un `File` que con cualquier objeto.
 */
export function createShotHolder<TFile>(revoke: (url: string) => void): {
  holdShots: (tradeId: string, shots: readonly PendingShot<TFile>[]) => void;
  takeShots: (tradeId: string) => readonly PendingShot<TFile>[];
} {
  let held: { readonly tradeId: string; readonly shots: readonly PendingShot<TFile>[] } | null =
    null;

  function discard(): void {
    if (held === null) return;

    for (const shot of held.shots) {
      if (shot.previewUrl !== undefined) revoke(shot.previewUrl);
    }

    held = null;
  }

  return {
    /** Lo que se pego en el alta, para el detalle de ESA operacion. Lo que
        quedara retenido de un alta anterior se descarta: ya no tiene destino. */
    holdShots(tradeId, shots) {
      discard();

      if (shots.length > 0) held = { tradeId, shots };
    },

    /**
     * Se recoge UNA vez: la segunda llamada no reencola lo ya encolado. Y solo
     * por la operacion a la que pertenece: si pregunta otra, lo retenido se
     * descarta, porque quien lo pego ya no esta yendo a donde iba.
     */
    takeShots(tradeId) {
      if (held === null) return [];

      const waiting = held;

      if (waiting.tradeId !== tradeId) {
        discard();

        return [];
      }

      held = null;

      return waiting.shots;
    },
  };
}

const holder = createShotHolder<File>((url) => URL.revokeObjectURL(url));

export const holdShots = holder.holdShots;
export const takeShots = holder.takeShots;
